import { useState } from 'react';
import {
  Box, Button, IconButton, Menu, MenuItem,
  ListItemIcon, Divider, CircularProgress, Typography,
} from '@mui/material';
import {
  HomeOutlined as HomeIcon,
  WorkOutline as WorkIcon,
  AssignmentOutlined as AssignmentIcon,
  PersonOutline as PersonIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getHostBoardSlug } from '../jobBoard/hostSlug';

const PURPLE = '#6A5AF9';

// Destino de la acción "Portal" (web de empleos): en subdominios
// {slug}.amandahr.ai la raíz ya es el board de esa organización.
const boardHref = () => (getHostBoardSlug() ? '/' : '/empleos');

/**
 * Topbar compartida del portal de candidatos y la web de empleos.
 *
 * - Con sesión: "Mis postulaciones" / "Mi perfil" + "Cerrar sesión".
 *   En pantallas chicas se colapsa en un menú de cuenta.
 * - Sin sesión (job board): botón "Ingresar" que lleva al portal.
 *
 * Dos modos de navegación:
 * - Portal (default): callbacks `onNavigate('my_applications' | 'profile')`
 *   sobre la máquina de estados de PortalLayout.
 * - `useLinks`: navegación por URL (/portal y /portal?view=profile), usada
 *   desde JobBoardLayout.
 *
 * `showBrand` muestra el logo de la plataforma a la izquierda; los padres lo
 * apagan cuando la página tiene branding del cliente (white-label).
 *
 * Desde una feria (PLAN_FERIAS), `fairSlug`: "Portal" vuelve a la feria y los
 * links mantienen ?feria=; `sessionEmail` muestra "Postulando como <correo>"
 * (importante si se usa una tablet compartida en el stand).
 */
const PortalHeader = ({
  hasSession = true,
  current = null,
  onNavigate,
  onLogout,
  loggingOut = false,
  useLinks = false,
  showBrand = false,
  fairSlug = null,
  sessionEmail = null,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);

  const fairQuery = fairSlug ? `feria=${encodeURIComponent(fairSlug)}` : '';
  const homeHref = fairSlug ? `/feria/${encodeURIComponent(fairSlug)}` : boardHref();
  const homeLabel = fairSlug ? t('portal.fair.headerLink') : t('portal.header.portal');
  const signInHref = fairSlug ? `/portal?${fairQuery}` : '/portal';

  const handleOpenMenu = (e) => setAnchorEl(e.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  const handleNavigate = (screen) => {
    handleCloseMenu();
    onNavigate?.(screen);
  };

  const handleLogout = () => {
    handleCloseMenu();
    onLogout?.();
  };

  // Opción activa marcada con "píldora" morada (visible de inmediato),
  // inactivas en gris neutro.
  const navButtonSx = (active) => ({
    textTransform: 'none',
    fontWeight: active ? 700 : 500,
    color: active ? PURPLE : '#6B7280',
    backgroundColor: active ? 'rgba(106,90,249,0.12)' : 'transparent',
    borderRadius: 999,
    px: 1.5,
    '&:hover': {
      backgroundColor: active ? 'rgba(106,90,249,0.18)' : 'rgba(0,0,0,0.04)',
    },
  });

  // Props de navegación por modo: href (job board) o onClick (portal)
  const LINK_HREFS = fairSlug
    ? {
      profile: `/portal?view=profile&${fairQuery}`,
      my_tests: `/portal?view=tests&${fairQuery}`,
      // Con ?feria= y sin vista, /portal vuelve a la feria: se pide la vista explícita
      my_applications: `/portal?view=applications&${fairQuery}`,
    }
    : {
      profile: '/portal?view=profile',
      my_tests: '/portal?view=tests',
      my_applications: '/portal',
    };
  const itemProps = (target) =>
    useLinks
      ? { href: LINK_HREFS[target] || '/portal' }
      : { onClick: () => handleNavigate(target) };

  return (
    <Box sx={{ backgroundColor: '#fff', borderBottom: '1px solid #E5E7EB' }}>
      {/* Full-width: las acciones quedan pegadas al borde derecho de la ventana */}
      <Box
        sx={{
          width: '100%',
          px: { xs: 2, md: 3 },
          height: 52,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        {/* Marca (solo cuando no hay branding del cliente) */}
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          {showBrand && (
            <Box
              component="img"
              src="/amandalogoh.png"
              alt="Amanda HR"
              sx={{ height: 22, display: 'block' }}
            />
          )}
          {hasSession && sessionEmail && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }} data-testid="portal-session-email">
              {t('portal.fair.signedInAs')} <b>{sessionEmail}</b>
            </Typography>
          )}
        </Box>

        {!hasSession ? (
          <Button
            variant="outlined"
            size="small"
            href={signInHref}
            startIcon={<PersonIcon fontSize="small" />}
            sx={{
              borderColor: PURPLE,
              color: PURPLE,
              textTransform: 'none',
              '&:hover': { borderColor: '#5B4DE8', backgroundColor: 'rgba(106,90,249,0.06)' },
            }}
          >
            {t('portal.header.signIn')}
          </Button>
        ) : (
          <>
            {/* Navegación en pantallas medianas+ */}
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5 }}>
              <Button
                size="small"
                color="inherit"
                startIcon={<HomeIcon fontSize="small" />}
                sx={navButtonSx(current === 'portal')}
                href={homeHref}
              >
                {homeLabel}
              </Button>
              <Button
                size="small"
                color="inherit"
                startIcon={<WorkIcon fontSize="small" />}
                sx={navButtonSx(current === 'my_applications')}
                {...itemProps('my_applications')}
              >
                {t('portal.header.myApplications')}
              </Button>
              <Button
                size="small"
                color="inherit"
                startIcon={<AssignmentIcon fontSize="small" />}
                sx={navButtonSx(current === 'my_tests')}
                {...itemProps('my_tests')}
              >
                {t('portal.header.myTests')}
              </Button>
              <Button
                size="small"
                color="inherit"
                startIcon={<PersonIcon fontSize="small" />}
                sx={navButtonSx(current === 'profile')}
                {...itemProps('profile')}
              >
                {t('portal.header.profile')}
              </Button>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1.25 }} />
              <Button
                size="small"
                color="inherit"
                startIcon={
                  loggingOut
                    ? <CircularProgress size={14} color="inherit" />
                    : <LogoutIcon fontSize="small" />
                }
                onClick={handleLogout}
                disabled={loggingOut}
                sx={{ textTransform: 'none', fontWeight: 500, color: '#6B7280', borderRadius: 999, px: 1.5 }}
              >
                {t('portal.header.logout')}
              </Button>
            </Box>

            {/* Menú compacto en móviles */}
            <Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
              <IconButton size="small" onClick={handleOpenMenu} aria-label={t('portal.header.account')}>
                <AccountIcon />
              </IconButton>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
                <MenuItem component="a" href={homeHref} selected={current === 'portal'}>
                  <ListItemIcon><HomeIcon fontSize="small" /></ListItemIcon>
                  {homeLabel}
                </MenuItem>
                <MenuItem
                  {...(useLinks ? { component: 'a' } : {})}
                  {...itemProps('my_applications')}
                  selected={current === 'my_applications'}
                >
                  <ListItemIcon><WorkIcon fontSize="small" /></ListItemIcon>
                  {t('portal.header.myApplications')}
                </MenuItem>
                <MenuItem
                  {...(useLinks ? { component: 'a' } : {})}
                  {...itemProps('my_tests')}
                  selected={current === 'my_tests'}
                >
                  <ListItemIcon><AssignmentIcon fontSize="small" /></ListItemIcon>
                  {t('portal.header.myTests')}
                </MenuItem>
                <MenuItem
                  {...(useLinks ? { component: 'a' } : {})}
                  {...itemProps('profile')}
                  selected={current === 'profile'}
                >
                  <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
                  {t('portal.header.profile')}
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout} disabled={loggingOut}>
                  <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                  {t('portal.header.logout')}
                </MenuItem>
              </Menu>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default PortalHeader;
